import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, LoginRequest } from '../services/authService';
import { setUnauthorizedHandler } from '../services/apiClient';
import { tokenStorage } from '../services/tokenStorage';
import { User } from '../types';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(() => tokenStorage.readUser<User>());
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const tokens = tokenStorage.read();
    const cachedUser = tokenStorage.readUser<User>();
    if (!tokens || !cachedUser) {
      tokenStorage.clear();
      setUser(null);
    } else {
      setUser(cachedUser);
    }
    setIsCheckingSession(false);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => logout);
  }, [logout]);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setIsAuthenticating(true);
      try {
        const response = await authService.login(credentials);
        tokenStorage.writeUser(response.user);
        setUser(response.user);
        navigate('/tasks', { replace: true });
      } finally {
        setIsAuthenticating(false);
      }
    },
    [navigate]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading: isCheckingSession || isAuthenticating,
      login,
      logout,
    }),
    [login, logout, user, isAuthenticating, isCheckingSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
